import React, { Component, ReactNode } from 'react';
import { Role, User } from '../../../domain/rbac/RbacDomains';
import CustomTable from '../../custom/CustomTable';
import { ColumnProps } from 'primereact/column';
import moment from 'moment/moment';
import Icon from '../../../icons/Icon';
import { DataTableRowClickEventParams } from 'primereact/datatable';
import { customConfirm } from '../../custom/CustomPopupConfirm';
import MessageUtil from '../../../util/MessageUtil';
import LanguageUtil from '../../../util/LanguageUtil';
import RoleHelper from '../../../RoleHelper';
import EnvironmentUtil from '../../../util/EnvironmentUtil';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import CustomTableContentWrapper from '../../custom/CustomTableContentWrapper';
import { Tooltip } from 'primereact/tooltip';
import { CheckerRequest } from '../../../domain/maker-checker/MakerCheckerDomains';
import ComponentName from '../../../constant/ComponentName';
import MakerCheckerClient from '../../../client/maker-checker/MakerCheckerClient';
import GatewayContainer from '../../../config/GatewayContainer';
import GatewayNames from '../../../constant/GatewayNames';

class RoleListComponent extends Component<Props> {
  private readonly environmentUtil: EnvironmentUtil = InstanceContainer.get(Injectables.ENVIRONMENT_UTIL);
  private readonly makerCheckerClient: MakerCheckerClient = GatewayContainer.getInstance().get(GatewayNames.MAKER_CHECKER_CLIENT);

  public render(): ReactNode {
    const { roles, selectedRole } = this.props;
    return (
      <CustomTable selectionMode={'single'} selection={selectedRole} dataKey='name' onRowClick={this.openEditor} columns={this.getColumns()} value={roles} />
    );
  }

  private openEditor = (e: DataTableRowClickEventParams) => {
    const { setSelectedRole, openEditor, setUpdateRequestData } = this.props;
    setSelectedRole(e.data);
    openEditor();
    this.makerCheckerClient.findByDocumentId(ComponentName.ADMIN_ROLE, e.data.name).then(setUpdateRequestData);
  };

  private getColumns = (): ColumnProps[] => {
    const columns = [
      {
        field: 'name',
        header: LanguageUtil.get({ id: 'common.input.placeholder.roleName', defaultMessage: 'Role Name' }),
        alignHeader: 'left',
        className: 'min-w-[23%]',
        body: (record: Role) => {
          return (
            <div className={'flex w-full items-center'}>
              <CustomTableContentWrapper text={record.name} />
            </div>
          );
        },
      },
      {
        field: 'description',
        header: LanguageUtil.get({ id: 'common.input.placeholder.roleDescription', defaultMessage: 'Role Description' }),
        className: 'min-w-[23%]',
        body: (record: Role) => {
          return (
            <div className={'flex w-full items-center'}>
              <CustomTableContentWrapper
                clipSize={340}
                showEllipsis={true}
                text={
                  record.description ||
                  LanguageUtil.get({ id: 'common.input.placeholder.roleNoDescription', defaultMessage: 'This role have not any description yet.' })
                }
              />
            </div>
          );
        },
      },
      {
        field: 'createdDate',
        header: LanguageUtil.get({ id: 'common.label.createdAt', defaultMessage: 'Created At' }),
        className: 'min-w-[23%]',
        body: (record: Role) => {
          return (
            <div className={'flex w-full items-center'}>
              <CustomTableContentWrapper text={moment(record.createdDate).format('LLLL').valueOf()} />
            </div>
          );
        },
      },
      {
        field: 'lastUpdated',
        header: LanguageUtil.get({ id: 'common.label.updatedAt', defaultMessage: 'Updated At' }),
        className: 'min-w-[23%]',
        body: (record: Role) => {
          return (
            <div className={'flex w-full items-center'}>
              <CustomTableContentWrapper text={moment(record.lastUpdated).format('LLLL').valueOf()} />
            </div>
          );
        },
      },
      {
        header: 'Action',
        field: LanguageUtil.get({ id: 'common.label.action', defaultMessage: 'Action' }),
        className: 'max-w-[75px] 2xl:max-w-[100px]',
        sortable: false,
        hidden: this.environmentUtil.isLdapOnly(),
        body: (record: Role) => {
          const { checkerRequests } = this.props;
          if (['Admin', 'Member'].includes(record.name)) return 'Readonly';
          const request = checkerRequests.find((c) => c.documentId == record.name);
          if (request && request.type == 'DELETE') {
            return;
          }
          return (
            <div
              className={'flex w-full justify-start items-center ml-2'}
              onClick={(e) => {
                const { onDelete } = this.props;
                e.preventDefault();
                e.stopPropagation();
                if (!RoleHelper.canManage('Role')) return;
                try {
                  customConfirm({
                    target: e.currentTarget,
                    accept: () => onDelete(record.name),
                    message: LanguageUtil.get({ id: 'messages.confirm.deleteRole', defaultMessage: 'Do you sure delete this role?' }),
                    acceptClassName: '!bg-primary !text-2xs',
                  });
                } catch (e) {
                  MessageUtil.error({
                    summary: LanguageUtil.get({ id: 'common.label.role', defaultMessage: 'Role' }),
                    detail: <div>{LanguageUtil.get({ id: 'messages.error.error', defaultMessage: 'Error.' })}</div>,
                  });
                }
              }}
            >
              <Tooltip position={'left'} target={'.remove'}>
                {LanguageUtil.get({ id: 'common.label.remove', defaultMessage: 'Remove' })}
              </Tooltip>
              <Icon icon={'delete'} size={20} className={'remove text-[#607080] hover:cursor-pointer'} />
            </div>
          );
        },
      },
    ];
    return columns.filter((column) => {
      if (column.field === 'action') {
        return RoleHelper.canEdit('Role');
      }
      return true;
    }) as ColumnProps[];
  };
}

interface Props {
  roles: Role[];
  selectedRole: Role | undefined;
  openEditor: VoidFunction;
  setSelectedRole: (role: Role) => void;
  onDelete: (name: string) => void;
  checkerRequests: CheckerRequest[];
  updateRequestData: CheckerRequest<Role> | undefined;
  setUpdateRequestData: (request: CheckerRequest | undefined) => void;
}

export default RoleListComponent;
